import { Injectable } from '@angular/core';
import { MsalService, MsalBroadcastService } from '@azure/msal-angular';
import { InteractionStatus, InteractionRequiredAuthError } from '@azure/msal-browser';
import { environment } from '@environments/environment';
import { Observable, BehaviorSubject, from, of } from 'rxjs';
import { filter, switchMap, catchError, shareReplay, tap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  private accessTokenSubject = new BehaviorSubject<string | null>(null);
  public accessToken$: Observable<string | null>;

  constructor(
    private msalService: MsalService,
    private msalBroadcastService: MsalBroadcastService
  ) {
    this.accessToken$ = this.initializeTokenObservable();
  }

  private initializeTokenObservable(): Observable<string | null> {
    return this.msalBroadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None),
        switchMap(() => {
          const activeAccount = this.msalService.instance.getActiveAccount();

          if (!activeAccount) {
            return of(null);
          }

          const accessTokenRequest = {
            scopes: environment.appConfig.common.custtpApiScopes,
            account: activeAccount,
          };

          return from(this.msalService.acquireTokenSilent(accessTokenRequest))
            .pipe(
              tap(response => {
                this.accessTokenSubject.next(response.accessToken);
              }),
              switchMap(response => of(response.accessToken)),
              catchError(error => {
                if (error instanceof InteractionRequiredAuthError) {
                  // Fallback to interactive method when silent acquisition fails
                  return from(this.msalService.acquireTokenPopup(accessTokenRequest))
                    .pipe(
                      tap(response => {
                        this.accessTokenSubject.next(response.accessToken);
                      }),
                      switchMap(response => of(response.accessToken)),
                      catchError(popupError => {
                        console.error('Interactive token acquisition failed:', popupError);
                        return of(null);
                      })
                    );
                }
                console.error('Silent token acquisition failed:', error);
                return of(null);
              })
            );
        }),
        shareReplay({ bufferSize: 1, refCount: true })
      );
  }

  /**
   * Returns a hot observable that shares a buffered access token.
   * Late subscribers will receive the latest token.
   */
  public getAccessToken(): Observable<string | null> {
    return this.accessToken$;
  }

  /**
   * Returns a Promise that resolves with an access token.
   * Rejects the promise if no user is signed in or if token acquisition fails.
   */
  public getAccessTokenAsPromise(): Promise<string | null> {
    return new Promise((resolve, reject) => {
      this.accessToken$.pipe(
        tap(token => {
          if (token) {
            resolve(token);
          } else {
            reject('No access token available or user not signed in.');
          }
        }),
        catchError(error => {
          console.error('Error acquiring access token:', error);
          reject(error);
          return of(null); // Return an observable to satisfy the pipe
        })
      ).subscribe();
    });
  }

  public isAdmin(): boolean {
    const activeAccount = this.msalService.instance.getActiveAccount();
    const idTokenClaims = activeAccount.idTokenClaims;

    if (!idTokenClaims) return false;

    const userRoles: string[] = (idTokenClaims['roles'] as string[]) || [];
    const adminRoles: string[] = environment.appConfig.adminRoles;

    return adminRoles.some(role => userRoles.includes(role));
  }
}
